import * as React from "react";
import {RestDataLoader} from "../../dashboard/widgets/unilever/RestDataLoader";
import {VeboxModels} from "../../types/veboxModels";
import {SelectInputOption} from "@vebox/etui-core";
import {FilterRawValues} from "../UserListFilter";
import {useCommonData} from "./CommonDataProvider";
import {VeboxUser} from "../../providers/VeboxUser";
import useCurrentUser = VeboxUser.useCurrentUser;

export type UserListContextType = {
    usersList: VeboxModels.User[],
    totalUser: number,
    isLoading: boolean,
    filter: FilterValues,
    rawFilter: FilterRawValues,
    filterParams: any,
    stringFilter: string,
    filterGuid: string,
    isFistRender: boolean,
    filtersUpdated: boolean,
    error: boolean,
    getUsersList: (offset?: number, append?: boolean) => void,
    updateFilters: (newFilter: FilterRawValues) => void
}

export const UserListContext = React.createContext<UserListContextType>({} as any)

export type FilterValues = {
    code: string,
    status: SelectInputOption,
    visibility: SelectInputOption,
    roles: string[],
    commercialHierarchy: string[]
    technicalHierarchy: string[]
    placeOwnershipHierarchy: string[]
    supplyHierarchy: string[]
    ownershipHierarchy: string[]
    customerHierarchy: string[]
}

export type usersFilter = {
    commercialHierarchyGuids?: string [],
    enabled?: boolean,
    guid?: string,
    guids?: string[],
    key?: {
        exactMatch?: boolean,
        key?: string,
        target?: "ANY"
    },
    notification?: string,
    ownershipHierarchyGuids?: string [],
    placeOwnershipHierarchyGuids?: string [],
    roleIn?: string [],
    roleOut?: string [],
    supplyHierarchyGuids?: string [],
    technicalHierarchyGuids?: string [],
    customerHierarchyGuids?: string [],
    visibility?: boolean
}

type HierarchiesForFilter = {
    commercialHierarchy: string[],
    ownershipHierarchy: string[],
    placeOwnershipHierarchy: string[],
    supplyHierarchy: string[],
    technicalHierarchy: string[],
    customerHierarchy: string[]
}

const createUsersFilter = (filters: FilterValues): usersFilter => {
    const uFilter: usersFilter = {};

    // Hierarchy filters
    uFilter.commercialHierarchyGuids = filters?.commercialHierarchy ?? [];
    uFilter.supplyHierarchyGuids = filters?.supplyHierarchy ?? [];
    uFilter.ownershipHierarchyGuids = filters?.ownershipHierarchy ?? [];
    uFilter.technicalHierarchyGuids = filters?.technicalHierarchy ?? [];
    uFilter.placeOwnershipHierarchyGuids = filters?.placeOwnershipHierarchy ?? [];
    uFilter.customerHierarchyGuids = filters?.customerHierarchy ?? [];


    // features
    if (filters?.code) {
        uFilter.key = {
            target: "ANY",
            key: filters.code
        }
    }
    if (filters?.visibility) {
        uFilter.visibility = filters.visibility.value === "true";
    }

    if (filters?.status) {
        uFilter.enabled = filters.status.value === "true"
    }

    // roles
    if (filters?.roles?.length > 0) {
        uFilter.roleIn = filters.roles;
    }

    return uFilter
}

export const UserListProvider: React.FC = (props) => {

    const [usersList, setUsersList] = React.useState<VeboxModels.User[]>([]);
    const [totalUser, setTotalUser] = React.useState<number>(0);
    const [isLoading, setIsLoading] = React.useState<boolean>(false);
    const [filter, setFilter] = React.useState<FilterValues>()
    const [rawFilter, setRawFilter] = React.useState<FilterRawValues>()
    const [stringFilter, setStringFilter] = React.useState<string>()
    const [filterGuid, setFilterGuid] = React.useState<string>()
    const [filterParams, setFilterParams] = React.useState<any>()
    const [error, setError] = React.useState<boolean>(false);
    const [isFistRender, setIsFistRender] = React.useState<boolean>(true);
    const [filtersUpdated, setFiltersUpdated] = React.useState<boolean>(false);
    const {hierarchiesTree} = useCommonData()
    const loggedInUser = useCurrentUser();

    const createHierarchiesForFilter = (hierarchyFilters: FilteredHierarchies): HierarchiesForFilter => {
        return {
            commercialHierarchy: getHierarchiesList(
                hierarchyFilters.commercialHierarchy ?? [],
                hierarchiesTree.find(h => h.hierarchyGuid === COMMERCIAL_HIERARCHY)
                ?? {hierarchyGuid: COMMERCIAL_HIERARCHY, label: "", children: []},
            ) ?? [],
            ownershipHierarchy: getHierarchiesList(
                hierarchyFilters.ownershipHierarchy ?? [],
                hierarchiesTree.find(h => h.hierarchyGuid === OWNERSHIP_HIERARCHY)
                ?? {hierarchyGuid: OWNERSHIP_HIERARCHY, label: "", children: []},
            ) ?? [],
            placeOwnershipHierarchy: getHierarchiesList(
                hierarchyFilters.placeOwnershipHierarchy ?? [],
                hierarchiesTree.find(h => h.hierarchyGuid === PLACE_OWNERSHIP_HIERARCHY)
                ?? {hierarchyGuid: PLACE_OWNERSHIP_HIERARCHY, label: "", children: []},
            ) ?? [],
            supplyHierarchy: getHierarchiesList(
                hierarchyFilters.supplyHierarchy ?? [],
                hierarchiesTree.find(h => h.hierarchyGuid === SUPPLY_HIERARCHY)
                ?? {hierarchyGuid: SUPPLY_HIERARCHY, label: "", children: []},
            ) ?? [],
            technicalHierarchy: getHierarchiesList(
                hierarchyFilters.technicalHierarchy ?? [],
                hierarchiesTree.find(h => h.hierarchyGuid === TECHNICAL_HIERARCHY)
                ?? {hierarchyGuid: TECHNICAL_HIERARCHY, label: "", children: []},
            ) ?? [],
            customerHierarchy: getHierarchiesList(
                hierarchyFilters.customerHierarchy ?? [],
                hierarchiesTree.find(h => h.hierarchyGuid === FAKE_CUSTOMER_HIERARCHY)
                ?? {hierarchyGuid: FAKE_CUSTOMER_HIERARCHY, label: "", children: []},
            ) ?? []
        };
    }

    const hasHierarchiesFilters = (): boolean => {
        return filter?.technicalHierarchy?.length > 0
            || filter?.placeOwnershipHierarchy?.length > 0
            || filter?.ownershipHierarchy?.length > 0
            || filter?.supplyHierarchy?.length > 0
            || filter?.commercialHierarchy?.length > 0
            || filter?.customerHierarchy?.length > 0
    }

    const updateFilters = (newFilter: FilterRawValues) => {
        setFiltersUpdated(true);
        setUsersList([]);
        setTotalUser(0);
        const hierarchyFilters = createHierarchiesForFilter(processHierarchiesForRequest(newFilter?.hierarchy ?? []));

        if ((newFilter?.hierarchy ?? []).some((hierarchy) => typeof hierarchy === "string"
            && hierarchy === loggedInUser.company.guid)) {
            hierarchyFilters.ownershipHierarchy.push(loggedInUser.company.guid);
        }

        if (newFilter !== undefined) {
            setFilter({
                status: newFilter.status,
                code: newFilter.code,
                roles: newFilter.roles,
                visibility: newFilter.visibility,
                commercialHierarchy: hierarchyFilters.commercialHierarchy,
                ownershipHierarchy: hierarchyFilters.ownershipHierarchy,
                placeOwnershipHierarchy: hierarchyFilters.placeOwnershipHierarchy,
                supplyHierarchy: hierarchyFilters.supplyHierarchy,
                technicalHierarchy: hierarchyFilters.technicalHierarchy,
                customerHierarchy: hierarchyFilters.customerHierarchy
            });
            setStringFilter(buildFilterListRequest([["roleIn", newFilter?.roles ?? []]]));
        } else {
            setFilter(undefined);
            setStringFilter("");
        }

        setRawFilter(newFilter);
    }
    const setUsersListResult = (list: VeboxModels.User[], total: number, append: boolean) => {
        if (append) {
            setUsersList([...usersList, ...list]);
        } else {
            setUsersList(list);
        }
        setIsFistRender(false);
        setFiltersUpdated(false);
        setTotalUser(total);
        setIsLoading(false);
    }

    const getUsersList = (offset: number = 0, append: boolean = true) => {
        if (!append) {
            setUsersList([]);
            setTotalUser(0);
        }
        setIsLoading(true)
        if (error) {
            setError(false)
        }
        const params = removeUndefinedFields({
            offset,
            key: filter?.code,
            target: "ANY",
            enabled: filter?.status?.value ? filter?.status?.value === "true" : undefined,
            visibility: filter?.visibility?.value ? filter?.visibility?.value === "true" : undefined
        });
        if (hasHierarchiesFilters()) {
            createFilterForUsersList(createUsersFilter(filter))
                .then(response => {
                    setFilterGuid(response.guid)
                    return getUsersListPromise("", {filterGuid: response.guid})
                })
                .then(({list, total}) => {
                    setUsersListResult(list, total, append);
                })
                .catch((err) => {
                    console.error(err)
                    setError(true);
                });
        } else {
            setFilterGuid("");
            setFilterParams(params);
            getUsersListPromise(stringFilter, params)
                .then(({list, total}) => {
                    setUsersListResult(list, total, append);
                })
                .catch((err) => {
                    console.error(err)
                    setError(true);
                })
        }
    }

    React.useEffect(() => {
        if (!isFistRender) {
            getUsersList();
        }
    }, [filter])

    React.useEffect(() => {
        getUsersList();
    }, [])

    return (
        <UserListContext.Provider
            value={{
                usersList,
                isLoading,
                totalUser,
                error,
                isFistRender,
                filtersUpdated,
                filter,
                rawFilter,
                filterParams,
                stringFilter,
                filterGuid,
                updateFilters,
                getUsersList
            }}
        >
            {props.children}
        </UserListContext.Provider>
    )
}

function encodeArrayHasURLParams<T>(arrayName: string, values: T[]) {
    return values.length === 0 ? "" : `${arrayName}=${values.join(`&${arrayName}=`)}`
}

type Params = [name: string, array: string[]]

export function buildFilterListRequest(params: Params[]) {
    return params
        .map(([name, array]) => encodeArrayHasURLParams(name, array))
        .filter(str => str.length > 0)
        .join("&")
}

function removeUndefinedFields<O extends {}>(obj: O) {
    return Object.keys(obj).reduce((partial, key) => {
        if (typeof obj[key] !== "undefined" || obj[key] !== null) {
            return {...partial, [key]: obj[key]}
        }
        return partial
    }, {})
}

const COMMERCIAL_HIERARCHY = "d58245f0-5294-11e8-a5e0-525400c5f801"
const TECHNICAL_HIERARCHY = "d5824708-5294-11e8-a5e0-525400c5f801"
const SUPPLY_HIERARCHY = "d5824820-5294-11e8-a5e0-525400c5f801"
const OWNERSHIP_HIERARCHY = "d5823d58-5294-11e8-a5e0-525400c5f801"
const PLACE_OWNERSHIP_HIERARCHY = "d582449c-5294-11e8-a5e0-525400c5f801"
const FAKE_CUSTOMER_HIERARCHY = "108dc9f5-3344-460c-82b8-0d3ff09e3741" // fake hierarchy to create a tree of companies under a customer-oriented company
export const HIERARCHY_TYPES_GUID = [COMMERCIAL_HIERARCHY, TECHNICAL_HIERARCHY, SUPPLY_HIERARCHY, OWNERSHIP_HIERARCHY, PLACE_OWNERSHIP_HIERARCHY, FAKE_CUSTOMER_HIERARCHY]

type FilteredHierarchies = {
    commercialHierarchy?: string[]
    technicalHierarchy?: string[]
    placeOwnershipHierarchy?: string[]
    supplyHierarchy?: string[]
    ownershipHierarchy?: string[]
    customerHierarchy?: string[]
}

function processHierarchiesForRequest(v: any[]) {
    return v.reduce<FilteredHierarchies>(
        (partial, node): FilteredHierarchies => {
            const hierarchyGuid = typeof node === "string" ? node : node.value
            const children = typeof node === "string" ? [hierarchyGuid] : treeToList(node.children)
            switch (hierarchyGuid) {
                case COMMERCIAL_HIERARCHY: {
                    return {...partial, commercialHierarchy: children}
                }
                case TECHNICAL_HIERARCHY: {
                    return {...partial, technicalHierarchy: children}
                }
                case PLACE_OWNERSHIP_HIERARCHY: {
                    return {...partial, placeOwnershipHierarchy: children}
                }
                case SUPPLY_HIERARCHY: {
                    return {...partial, supplyHierarchy: children}
                }
                case OWNERSHIP_HIERARCHY: {
                    return {...partial, ownershipHierarchy: children}
                }
                case FAKE_CUSTOMER_HIERARCHY: {
                    return {...partial, customerHierarchy: children}
                }
                default : {
                    return {...partial, ownershipHierarchy: children}
                }
            }
            return partial
        },
        {}
    )
}

type CommercialHierarchyNode = {
    value: string,
    children: (string | CommercialHierarchyNode)[],
}

function treeToList(hierarchyTree: CommercialHierarchyNode[]) {
    const treeLeafReducer = (partial: string[], ht: string | CommercialHierarchyNode) => {
        if (typeof ht === 'string') {
            return [...partial, ht];
        }
        return [...partial, ...ht.children.reduce(treeLeafReducer, [] as string[])]
    };

    return hierarchyTree.reduce(treeLeafReducer, [] as string[]);
}

export const equalsArrays = (first: any[], second: any[]): boolean => {
    let equals = true
    const tmp1 = first?.length > 0 ? first : undefined;
    const tmp2 = second?.length > 0 ? second : undefined;

    if (tmp1 !== undefined && tmp2 !== undefined && tmp1.length === tmp2.length) {
        tmp1?.forEach((h) => {
            if (tmp2?.findIndex(h1 => h === h1) === -1) {
                equals = false;
            }
        })
    } else {
        return tmp1 === undefined && tmp2 === undefined;
    }

    return equals;
}

export const equalsFilter = (rawFilter: FilterRawValues, filters: FilterRawValues): boolean => {
    return rawFilter?.visibility?.value === filters?.visibility?.value
        && rawFilter?.status?.value === filters?.status?.value
        && (rawFilter?.code === filters?.code)
        && equalsArrays(rawFilter?.hierarchy, filters?.hierarchy)
        && equalsArrays(rawFilter?.roles, filters?.roles);
}

export const hasFilters = (filters: FilterRawValues): boolean => {
    return filters
        ? ((filters?.code !== undefined && filters?.code !== null)
            || (filters?.visibility !== undefined && filters?.visibility !== null)
            || (filters?.status !== undefined && filters?.status !== null)
            || filters?.hierarchy?.length > 0
            || filters?.roles?.length > 0)
        : false
}

function getHierarchiesList(hierarchiesList: string[], hierarchiesTree: VeboxModels.HierarchiesTree) {
    return hierarchiesList.reduce<string[]>((acc, hierarchyGuid) => {
        return [
            ...acc,
            ...getSubTreeNodes(
                hierarchyGuid,
                hierarchiesTree
            )
        ]
    }, [])
}

function getSubTreeNodes(parentGuid: string, tree: VeboxModels.HierarchiesTree): string[] {
    if (tree.hierarchyGuid === parentGuid) {
        return [tree.hierarchyGuid, ...getAllTreeNodes(tree)]
    }
    return tree.children.reduce<string[]>((acc, childTree) => [...acc, ...getSubTreeNodes(parentGuid, childTree)], [])
}

function getAllTreeNodes(tree: VeboxModels.HierarchiesTree): string[] {
    return tree.children.reduce<string[]>(
        (acc, subTree) => [...acc, subTree.hierarchyGuid, ...getAllTreeNodes(subTree)], [])
}

interface UserListResponse {
    list: VeboxModels.User[],
    total: number
}

const getUsersListPromise = (stringFilter: string, params: any): Promise<UserListResponse> => {
    return new Promise<UserListResponse>((resolve, reject) => {
        RestDataLoader.loadData(
            `users?${stringFilter}`,
            params,
            resolve,
            reject,
        );
    })
}


const createFilterForUsersList = (params: any): Promise<usersFilter> => {
    return new Promise<usersFilter>((resolve, reject) => {
        RestDataLoader.postJSON(
            `filters/users`,
            params,
            resolve,
            reject,
        );
    })
}


